/**
 * LIVE finance recommendations — the pure, unit-testable selection + mapping layer behind the
 * `recommendations` (exit) screen.
 *
 * The static `RECOMMENDATIONS` table in `./recommendations.ts` is a hand-curated snapshot of the
 * finance catalog (sourced 2026-06-14). This module replaces that snapshot with the LIVE catalog:
 * it filters the platform's `GET externships` payload down to finance-active programs, buckets them
 * by the user's archetype, and maps each to the existing `Recommendation` shape so the screen renders
 * unchanged. The static table remains the graceful fallback (wired in `useFinanceRecommendations`).
 *
 * Everything here is PURE (no React, no fetch) so it can be exhaustively unit-tested. The hook that
 * feeds it the catalog data — and that falls back to the static table on any failure — lives in
 * `../_hooks/use-finance-recommendations.ts`.
 */

import type { Externship } from '@/infra/schemas/externship';

import type { ArchetypeId } from './archetypes';
import { REC_IMAGES, type Recommendation } from './recommendations';

/**
 * The catalog `tags.categories` values that mark an externship as "finance". Matched
 * CASE-INSENSITIVELY against each externship's `tags.categories`. Sourced from the live finance
 * programs' category taxonomy (the same categories the static snapshot was drawn from).
 */
export const FINANCE_CATEGORIES = [
  'Finance',
  'Financial Planning & Analysis',
  'Value Investing & Financial Modeling',
  'Deal Sourcing (Venture Capital)',
  'Real Estate Finance & Valuation',
] as const;

/**
 * Per-archetype PRIORITY finance subtypes (a subset of {@link FINANCE_CATEGORIES}). Programs whose
 * categories intersect these are surfaced FIRST for that archetype; the rest of the finance-active
 * pool backfills behind them. `gen` has no priority subtype → any finance-active program qualifies
 * equally (pure soonest-start order).
 *
 *  - corp   (THE SCALER)  → operator-side: FP&A + real-estate valuation
 *  - invest (THE ANALYST) → markets-side: value investing + venture deal sourcing
 *  - gen    (THE POLYMATH)→ any finance program
 */
export const ARCHETYPE_PRIORITY_SUBTYPES: Record<ArchetypeId, readonly string[]> = {
  corp: ['Financial Planning & Analysis', 'Real Estate Finance & Valuation'],
  invest: ['Value Investing & Financial Modeling', 'Deal Sourcing (Venture Capital)'],
  gen: [],
};

/** Max recommendation cards the exit screen renders (1 best-match + 2 vertical). */
const MAX_RECOMMENDATIONS = 3;

/** Lower-cased copy of {@link FINANCE_CATEGORIES} for case-insensitive intersection checks. */
const FINANCE_CATEGORIES_LOWER = new Set(FINANCE_CATEGORIES.map((c) => c.toLowerCase()));

/** True iff any of `categories` (case-insensitively) appears in `pool` (a lower-cased Set). */
function intersectsLower(categories: readonly string[], pool: ReadonlySet<string>): boolean {
  return categories.some((c) => pool.has(c.toLowerCase()));
}

/**
 * True iff `categories` contains any finance category (case-insensitive). The SINGLE source of truth
 * for "is this finance?" — shared by the recommendations filter ({@link isFinanceActive}) AND the Labs
 * entry gating (catalog Finance-filter detection + finance-enrollment check in `use-finance-labs-entry`)
 * so the finance taxonomy never drifts apart between the two call sites.
 */
export function isFinanceCategoryList(categories: readonly string[]): boolean {
  return intersectsLower(categories, FINANCE_CATEGORIES_LOWER);
}

/**
 * A finance-active program: `status === 'ACTIVE'` AND its `tags.categories` intersects
 * {@link FINANCE_CATEGORIES} (case-insensitive).
 */
export function isFinanceActive(ext: Externship): boolean {
  return ext.status === 'ACTIVE' && intersectsLower(ext.tags.categories, FINANCE_CATEGORIES_LOWER);
}

/**
 * Parse a catalog `startDate` to a sortable epoch. The schema (ExternshipDateSchema →
 * formatDateWithDot) transforms `startDate` into a human-formatted en-US string WITH A COMMA, e.g.
 * "Jun 15, 2026", or `''` when absent; an empty / unparseable value sorts LAST (`+Infinity`) so
 * dated programs always lead.
 *
 * NOTE: the `Externship` schema exposes ONLY this display-formatted string — there is no raw/ISO
 * start date on the type — so we re-parse the display string. `new Date("Jun 15, 2026")` is a
 * date-only parse that resolves to LOCAL midnight (locale-fragile), so the resulting epoch is NOT a
 * true UTC instant. That's acceptable here because we only use it for a RELATIVE soonest-first sort
 * (every value parses the same way, so the ordering is stable); do not treat it as an absolute time.
 */
function startDateMs(ext: Externship): number {
  if (!ext.startDate) {
    return Number.POSITIVE_INFINITY;
  }
  const ms = new Date(ext.startDate).getTime();
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/** Stable sort by soonest start date (earliest first); undated programs trail. */
function bySoonestStart(a: Externship, b: Externship): number {
  return startDateMs(a) - startDateMs(b);
}

/**
 * Select up to {@link MAX_RECOMMENDATIONS} externships for an archetype:
 *   1. Filter to the finance-active pool.
 *   2. PRIORITY tier — programs intersecting the archetype's priority subtypes.
 *   3. BACKFILL tier — every other finance-active program.
 *   4. Sort EACH tier by soonest start date, concatenate (priority first), dedupe by `id`, take ≤3.
 *
 * `gen` has no priority subtypes, so its whole pool falls through to the backfill tier (pure
 * soonest-start order) — equivalent behaviour, just one tier.
 */
export function selectForArchetype(externships: readonly Externship[], archetype: ArchetypeId): Externship[] {
  const pool = externships.filter(isFinanceActive);
  const prioritySubtypes = new Set(ARCHETYPE_PRIORITY_SUBTYPES[archetype].map((s) => s.toLowerCase()));

  const priority: Externship[] = [];
  const backfill: Externship[] = [];
  for (const ext of pool) {
    if (prioritySubtypes.size > 0 && intersectsLower(ext.tags.categories, prioritySubtypes)) {
      priority.push(ext);
    } else {
      backfill.push(ext);
    }
  }

  priority.sort(bySoonestStart);
  backfill.sort(bySoonestStart);

  const seen = new Set<number>();
  const selected: Externship[] = [];
  for (const ext of [...priority, ...backfill]) {
    if (seen.has(ext.id)) {
      continue;
    }
    seen.add(ext.id);
    selected.push(ext);
    if (selected.length >= MAX_RECOMMENDATIONS) {
      break;
    }
  }
  return selected;
}

/** Distinct, order-preserving copy of `values` (drops empties + duplicates). */
function unique(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (v && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/**
 * Derive the "What you'll learn" bullets: the program's `details.responsibilities` mapped to
 * strings (a responsibility is either a bare string or `{ title, description }` → its description),
 * filtering empties, capped at 4. If there are no responsibilities, fall back to the first 4 skills.
 */
function deriveWhatYoullLearn(ext: Externship): string[] {
  const responsibilities = ext.details?.responsibilities ?? [];
  const fromResponsibilities = responsibilities
    .map((r) => (typeof r === 'string' ? r : r.description))
    .filter((s): s is string => Boolean(s))
    .slice(0, 4);
  if (fromResponsibilities.length > 0) {
    return fromResponsibilities;
  }
  return ext.tags.skills.slice(0, 4);
}

/**
 * Map a live `Externship` to the existing `Recommendation` shape the exit screen renders. Every
 * field degrades gracefully: company name falls back through the `company.{name,title}` variants
 * (the Zod transform already collapses the raw `companyName`/`companyTitle`/`companyLogo` columns
 * into `company`), the image through card → hero → top-image → the generic backstop, and the logo
 * to '' (the <img> chip handles a missing src). `tags` are unique(skills + topics) capped at 5
 * (duration is its own chip, kept out).
 */
export function toRecommendation(ext: Externship): Recommendation {
  return {
    company: ext.company?.name || ext.company?.title || '',
    title: ext.title,
    // Real catalog id → the exit CTA deep-links to this program's detail/enroll page.
    externshipId: ext.id,
    duration: ext.weeksDuration ? `${ext.weeksDuration} weeks` : '',
    image: ext.cardImage || ext.image || ext.details?.topImage || REC_IMAGES.best,
    logo: ext.company?.logo || '',
    blurb: ext.description || ext.details?.overview || '',
    tags: unique([...ext.tags.skills, ...ext.tags.topics]).slice(0, 5),
    whatYoullLearn: deriveWhatYoullLearn(ext),
  };
}

/**
 * The full pure pipeline: select the archetype's finance-active programs from the catalog and map
 * them to `Recommendation`s (≤3). Returns `[]` when the catalog has no finance-active match for the
 * archetype — the caller (`useFinanceRecommendations`) treats an empty result as "use the static
 * fallback".
 */
export function buildLiveRecommendations(externships: readonly Externship[], archetype: ArchetypeId): Recommendation[] {
  return selectForArchetype(externships, archetype).map(toRecommendation);
}
